import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TrendingUp } from "lucide-react";

import AppEmptyState from "@/components/ui/AppEmptyState";
import { computeYtdFromMonthlySeries } from "@/lib/businessRules";
import { formatPilotageCurrency } from "@/lib/pilotageUi";

const ForecastRevenueChart = ({ data = [] }) => {
  const chartData = computeYtdFromMonthlySeries(data);
  const hasForecast = chartData.some((entry) => entry.ca > 0);
  const annualTotal = chartData.reduce((sum, entry) => sum + Number(entry.ca || 0), 0);
  const peakMonth = chartData.reduce(
    (currentPeak, entry) => Number(entry.ca || 0) > Number(currentPeak?.ca || 0) ? entry : currentPeak,
    null,
  );
  const activeMonths = chartData.filter((entry) => Number(entry.ca || 0) > 0).length;

  return (
    <section
      className="min-w-0 rounded-3xl border border-white/10 glass p-6"
      data-testid="pilotage-forecast-revenue"
    >
      <div className="min-w-0">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
          <TrendingUp className="h-5 w-5 text-lenvia-blue" />
          Prévision CA mensuel (année en cours)
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          Estimation mensuelle: jours déclarés en priorité, sinon prévision.
        </p>
      </div>

      <div className="mt-5 min-w-0">
        {hasForecast ? (
          <div className="space-y-5">
            <p className="text-sm leading-relaxed text-slate-300" data-testid="pilotage-forecast-summary">
              Prévision annuelle de {formatPilotageCurrency(annualTotal)} sur {activeMonths} mois actifs.
              {peakMonth ? ` Le niveau le plus élevé est attendu en ${peakMonth.name} avec ${formatPilotageCurrency(peakMonth.ca)}.` : ""}
            </p>

            <div
              className="h-[280px] w-full min-w-0 sm:h-[300px]"
              aria-hidden="true"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff15" vertical={false} />
                  <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis
                    stroke="#94A3B8"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `${Math.round(value / 1000)}k`}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(255,255,255,0.05)" }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const point = payload[0]?.payload;
                      return (
                        <div className="rounded-2xl border border-white/10 bg-slate-950/95 p-3 shadow-xl">
                          {label ? <p className="text-sm font-medium text-white">{label}</p> : null}
                          <p className="mt-1 text-sm text-slate-200">CA estimé : {formatPilotageCurrency(point?.ca)}</p>
                          <p className="mt-1 text-sm text-lenvia-blue">CA cumulé (YTD) : {formatPilotageCurrency(point?.ytd)}</p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="ca" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <details className="rounded-xl border border-white/10 bg-white/[0.02]">
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lenvia-blue">
                Consulter les prévisions mensuelles
              </summary>
              <div className="overflow-x-auto border-t border-white/10">
                <table className="w-full min-w-[440px] text-left text-sm">
                  <caption className="sr-only">Prévisions mensuelles de chiffre d'affaires</caption>
                  <thead className="text-slate-300"><tr><th className="px-4 py-3" scope="col">Mois</th><th className="px-4 py-3 text-right" scope="col">CA estimé</th><th className="px-4 py-3 text-right" scope="col">Cumul annuel</th></tr></thead>
                  <tbody className="divide-y divide-white/[0.06]">
                    {chartData.map((entry) => <tr key={entry.key || entry.name}><th className="px-4 py-3 font-normal text-slate-200" scope="row">{entry.name}</th><td className="px-4 py-3 text-right tabular-nums text-white">{formatPilotageCurrency(entry.ca)}</td><td className="px-4 py-3 text-right tabular-nums text-slate-300">{formatPilotageCurrency(entry.ytd)}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        ) : (
          <AppEmptyState
            title="Aucune projection disponible"
            description="Aucune mission active ne contribue au CA estimé sur l'année en cours."
            className="p-6"
            testId="pilotage-forecast-empty"
          />
        )}
      </div>
    </section>
  );
};

export default ForecastRevenueChart;
